"use client"

import { useState, useRef, useEffect } from "react"
import Image from "next/image"

interface BeforeAfterSliderProps {
  beforeImage: string
  afterImage: string
  beforeAlt?: string
  afterAlt?: string
  className?: string
}

export function BeforeAfterSlider({
  beforeImage,
  afterImage,
  beforeAlt = "Before",
  afterAlt = "After",
  className = "",
}: BeforeAfterSliderProps) {
  const [sliderPosition, setSliderPosition] = useState(50)
  const isDraggingRef = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const initialXRef = useRef(0)
  const initialPositionRef = useRef(50)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const hasDraggedRef = useRef(false)

  // Setup event listeners
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    function calculatePosition(clientX: number): number {
      if (!container) return 50
      const rect = container.getBoundingClientRect()
      const x = clientX - rect.left
      return Math.max(0, Math.min(100, (x / rect.width) * 100))
    }

    // Handle initial click/touch
    function handleStart(clientX: number) {
      isDraggingRef.current = true
      initialXRef.current = clientX
      initialPositionRef.current = sliderPosition
      document.body.style.userSelect = "none" // Prevent text selection during drag
    }

    // Handle move during drag
    function handleMove(clientX: number) {
      if (!isDraggingRef.current || !container) return

      const rect = container.getBoundingClientRect()
      const deltaX = clientX - initialXRef.current
      const deltaPercent = (deltaX / rect.width) * 100
      const newPosition = Math.max(0, Math.min(100, initialPositionRef.current + deltaPercent))

      setSliderPosition(newPosition)
    }

    // Handle end of drag
    function handleEnd() {
      isDraggingRef.current = false
      document.body.style.userSelect = ""
      touchStartRef.current = null
      hasDraggedRef.current = false
    }

    // Mouse events
    function onMouseDown(e: MouseEvent) {
      // Only handle left mouse button
      if (e.button !== 0) return

      // Jump to click position first
      setSliderPosition(calculatePosition(e.clientX))

      // Then start dragging
      handleStart(e.clientX)
      e.preventDefault()
    }

    function onMouseMove(e: MouseEvent) {
      handleMove(e.clientX)
    }

    // Touch events
    function onTouchStart(e: TouchEvent) {
      if (e.touches.length !== 1) return

      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      }
      hasDraggedRef.current = false

      // Jump to touch position first
      setSliderPosition(calculatePosition(e.touches[0].clientX))

      // Then start dragging
      handleStart(e.touches[0].clientX)
    }

    function onTouchMove(e: TouchEvent) {
      if (e.touches.length !== 1) return

      if (touchStartRef.current && !hasDraggedRef.current) {
        const deltaX = Math.abs(e.touches[0].clientX - touchStartRef.current.x)
        const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y)

        // If vertical movement is greater than horizontal, allow scrolling
        if (deltaY > deltaX && deltaY > 10) {
          return // Allow natural scrolling
        }

        // If horizontal movement exceeds threshold, start preventing default
        if (deltaX > 10) {
          hasDraggedRef.current = true
        }
      }

      // Only prevent default if we've determined this is a drag gesture
      if (hasDraggedRef.current) {
        handleMove(e.touches[0].clientX)
        e.preventDefault() // Prevent scrolling during slider drag
      }
    }

    // Add all events
    const handle = container.querySelector(".slider-handle") as HTMLElement
    if (handle) {
      handle.addEventListener("mousedown", onMouseDown)
      handle.addEventListener("touchstart", onTouchStart, { passive: false })
    }

    // Also allow clicking anywhere on the container
    container.addEventListener("mousedown", onMouseDown)
    container.addEventListener("touchstart", onTouchStart, { passive: false })

    // Global events to handle drag end and movement outside the container
    window.addEventListener("mousemove", onMouseMove)
    window.addEventListener("touchmove", onTouchMove, { passive: false })
    window.addEventListener("mouseup", handleEnd)
    window.addEventListener("touchend", handleEnd)
    window.addEventListener("touchcancel", handleEnd)

    return () => {
      // Clean up all events
      if (handle) {
        handle.removeEventListener("mousedown", onMouseDown)
        handle.removeEventListener("touchstart", onTouchStart)
      }
      container.removeEventListener("mousedown", onMouseDown)
      container.removeEventListener("touchstart", onTouchStart)
      window.removeEventListener("mousemove", onMouseMove)
      window.removeEventListener("touchmove", onTouchMove)
      window.removeEventListener("mouseup", handleEnd)
      window.removeEventListener("touchend", handleEnd)
      window.removeEventListener("touchcancel", handleEnd)
      document.body.style.userSelect = ""
    }
  }, [sliderPosition]) // Re-initialize when slider position changes

  return (
    <div ref={containerRef} className={`relative overflow-hidden cursor-ew-resize select-none ${className}`}>
      {/* After Image (Background) */}
      <div className="relative w-full h-full">
        <Image src={afterImage || "/placeholder.svg"} alt={afterAlt} fill className="object-cover" priority />

        {/* Before Image (Clipped overlay) */}
        <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}>
          <Image src={beforeImage || "/placeholder.svg"} alt={beforeAlt} fill className="object-cover" priority />
        </div>
      </div>

      {/* Slider Line and Handle */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-10 pointer-events-none"
        style={{ left: `${sliderPosition}%` }}
      >
        {/* Slider Handle */}
        <div className="slider-handle absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg border-2 border-gray-300 cursor-ew-resize pointer-events-auto flex items-center justify-center">
          <div className="flex space-x-0.5">
            <div className="w-0.5 h-4 bg-gray-400"></div>
            <div className="w-0.5 h-4 bg-gray-400"></div>
          </div>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 bg-black/50 text-white px-2 py-1 rounded text-sm font-medium">Before</div>
      <div className="absolute top-4 right-4 bg-black/50 text-white px-2 py-1 rounded text-sm font-medium">After</div>

      {/* Instructions */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded text-xs">
        Drag to compare
      </div>
    </div>
  )
}
